import React, { useEffect, useState } from "react";
import Icon from "../shared/Icon";
import ResidentPicker from "./ResidentPicker";
import { LoadingRows } from "../shared/States";

/**
 * Source des étiquettes : import CSV, saisie manuelle, et liste éditable.
 *
 * C'est le chemin NORMAL de l'écran — les étiquettes ne viennent pas du
 * back-end par défaut. Le chargement depuis les commandes du point de collecte
 * existe en second recours, piloté depuis l'écran parent.
 *
 * `papaparse` est en import dynamique : il n'est chargé qu'au premier fichier
 * choisi, et reste donc hors du bundle initial.
 */

/** La résidence est pré-remplie avec l'établissement du manager, quand il est connu. */
const emptyForm = (residence = "") => ({
  woshId: "",
  firstName: "",
  lastName: "",
  residence,
  pages: 1,
});

const ResidentsPanel = ({
  entries,
  directory,
  addedIds,
  onPick,
  residenceName,
  residenceOptions,
  activeId,
  mode,
  isLoading,
  onAdd,
  onImport,
  onSelect,
  onRemove,
  t,
}) => {
  const [form, setForm] = useState(() => emptyForm(residenceName));

  // Le nom d'établissement arrive après le chargement du profil, alors que le
  // formulaire s'initialise au premier rendu. On le rattrape — mais seulement
  // si le champ est encore vide, pour ne jamais écraser une saisie en cours.
  useEffect(() => {
    if (!residenceName) return;
    setForm((current) => (current.residence ? current : { ...current, residence: residenceName }));
  }, [residenceName]);

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const isValid = form.woshId && form.lastName;

  const submit = (event) => {
    event.preventDefault();
    if (!isValid) return;
    onAdd(form);
    setForm(emptyForm(residenceName));
  };

  return (
    <div className="mgr-lbl-source">
      <div className="mgr-label">{t("Add labels")}</div>

      {/* Chemin le plus court d'abord : l'annuaire du point de collecte est
          déjà chargé, choisir quelqu'un suffit. L'import CSV et la saisie
          manuelle couvrent ce qu'il ne contient pas. */}
      <ResidentPicker
        residents={directory || []}
        alreadyAdded={addedIds || new Set()}
        onPick={onPick}
        isLoading={isLoading}
        t={t}
      />

      <div className="mgr-lbl-or">
        <span>{t("or enter manually")}</span>
      </div>

      <form onSubmit={submit} className="mgr-lbl-form">
        <label>
          <span>{t("WOSH-ID")}</span>
          <input value={form.woshId} onChange={set("woshId")} placeholder="MG47783" />
        </label>

        <div className="mgr-lbl-form-2">
          <label>
            <span>{t("First name")}</span>
            <input value={form.firstName} onChange={set("firstName")} placeholder="Jean" />
          </label>
          <label>
            <span>{t("Last Name")}</span>
            <input value={form.lastName} onChange={set("lastName")} placeholder="Dupont" />
          </label>
        </div>

        {/* Chambre et nombre de planches sur la même ligne : un champ pleine
            largeur pour un nombre à un chiffre déséquilibrait la colonne. */}
        <div className={`mgr-lbl-form-room${mode === "fullPages" ? " has-sheets" : ""}`}>
          <label>
            <span>{t("Residence")}</span>
            {/* Ce champ part à l'impression sur l'étiquette. Quand le manager
                est responsable de plusieurs établissements, il choisit lequel ;
                sinon il reste libre de le corriger à la main — c'est le seul
                recours tant que le serveur ne renvoie pas son périmètre. */}
            {(residenceOptions || []).length > 1 ? (
              <select value={form.residence} onChange={set("residence")}>
                {residenceOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={form.residence}
                onChange={set("residence")}
                placeholder={residenceName || "WZC De Linde"}
              />
            )}
          </label>

          {mode === "fullPages" ? (
            <label>
              <span>{t("Sheets")}</span>
              <input type="number" min="1" max="50" value={form.pages} onChange={set("pages")} />
            </label>
          ) : null}
        </div>

        <button type="submit" className="mgr-btn-primary mgr-lbl-add" disabled={!isValid}>
          <Icon name="person-add" />
          {t("Add")}
        </button>
      </form>

      <div className="mgr-label mgr-lbl-listtitle">
        {t("Labels to print")} <span className="mgr-lbl-count">{entries.length}</span>
      </div>

      {isLoading ? (
        <LoadingRows rows={4} height={44} />
      ) : entries.length === 0 ? (
        <p className="mgr-note">{t("Nothing yet — import a CSV or add someone above.")}</p>
      ) : (
        <ul className="mgr-lbl-list">
          {entries.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className={`mgr-lbl-item${activeId === entry.id ? " is-active" : ""}`}
                onClick={() => onSelect(entry.id)}
                aria-pressed={activeId === entry.id}
              >
                <span className="mgr-lbl-item-body">
                  <span className="mgr-lbl-item-name">
                    {entry.firstName} {entry.lastName}
                  </span>
                  <span className="mgr-lbl-item-meta">
                    {entry.woshId}
                    {mode === "fullPages" ? ` · ${entry.pages} ${t("sheets")}` : ""}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="mgr-lbl-remove"
                aria-label={`${t("Remove")} ${entry.firstName} ${entry.lastName}`}
                onClick={() => onRemove(entry.id)}
              >
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {mode === "ecoSheet" && entries.length > 0 ? (
        <p className="mgr-note">{t("Select someone, then click the cells to fill.")}</p>
      ) : null}
    </div>
  );
};

export default ResidentsPanel;
